import { useState } from 'react';

import { ArrowUpRight } from 'lucide-react';

import { Copy } from '@/shadcn/copy';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/shadcn/dialog';

type Props = {
  traceId: string;
  traceName?: string | null;
  error: unknown;
};

// A batch item's error is opaque JSON from the engine: sometimes { message },
// sometimes a nested cause, sometimes neither. Pull out the human line if there
// is one, and keep the raw payload for the dialog.
export function errorSummary(error: unknown): string | null {
  if (!(error && typeof error === 'object')) {
    return typeof error === 'string' ? error : null;
  }
  if ('message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return null;
}

function rawError(error: unknown): string {
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}

/**
 * Failure detail for one batch item. The whole truncated line is the target —
 * hovering the row reveals a quiet "See more" and opens the full payload on
 * click, copyable, because the useful part is usually an id or a code buried
 * inside it.
 */
export function BatchItemErrorDetail({ traceId, traceName, error }: Props) {
  const [open, setOpen] = useState(false);
  const summary = errorSummary(error);
  const raw = rawError(error);
  const label = traceName ?? traceId;

  if (!summary) {
    return null;
  }

  return (
    <>
      {/* The line itself, not a small button beside it: a one-line message in a
          dense feed is the only comfortable target. Tinted with the row rather
          than accented — a blue link inside a red card fights it. */}
      <button
        className="group/error mt-0.5 flex w-full min-w-0 cursor-pointer items-center gap-1.5 text-left"
        data-testid={`batch-item-error-${traceId}`}
        onClick={() => setOpen(true)}
        type="button"
      >
        <span
          className="min-w-0 truncate text-[11px] text-foreground/50 transition-colors group-hover/error:text-foreground/80"
          data-testid={`batch-item-error-message-${traceId}`}
        >
          {summary}
        </span>
        <span
          className="flex shrink-0 items-center gap-0.5 text-[11px] text-foreground/50 opacity-0 transition-opacity group-hover/error:text-foreground/80 group-hover/error:underline group-hover:opacity-100"
          data-testid={`batch-item-error-more-${traceId}`}
        >
          See more
          <ArrowUpRight aria-hidden className="size-3" />
        </span>
      </button>

      <Dialog onOpenChange={setOpen} open={open}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>Failed</span>
              <span className="font-mono text-foreground/60 text-xs">
                {label}
              </span>
            </DialogTitle>
            <DialogDescription className="whitespace-pre-wrap break-words text-left">
              {summary}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between">
            <span className="font-semibold text-foreground/50 text-xs uppercase">
              Raw error
            </span>
            <Copy textToCopy={raw} tooltipText="Copy the raw error" />
          </div>
          <pre
            className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-[11px] leading-relaxed"
            data-testid={`batch-item-error-raw-${traceId}`}
          >
            {raw}
          </pre>
        </DialogContent>
      </Dialog>
    </>
  );
}
